import { _ } from "../../../../core/polyglot/polyglot.js";
import DimensionSpecification from "../../../dimensionSpecification/DimensionSpecification.js";
import GroupRowTemplate from "./GroupRowTemplate.js";
import OrSpecification from "../../../specification/OrSpecification.js";
import RepeatingRow from "../../reports/rows/RepeatingRow.js";

/**
 * The purpose of `repeatingRowTemplate` is to repeat a set of
 * rows accross different dimensions.
 *
 * During interpretation, each child row template is instantiated
 * once for each dimension.
 *
 * @param {string[]} spec.dimensionIds - Dimensions over which to
 * clone children.
 */
export default class RepeatingRowTemplate extends GroupRowTemplate {
	constructor({ dimensionIds = [] } = {}) {
		super(...arguments);

		this._dimensionIds = dimensionIds;
	}

	getTitle() {
		// Ignore this._title because the repeating row is not
		// visible and should then not have a title on its own.
		return this.constructor.getLabel();
	}

	getDimensionIds() {
		return this._dimensionIds;
	}

	getDimensionSpecification() {
		const specifications = this._dimensionIds.map(
			(dimensionId) => new DimensionSpecification({ dimensionId }),
		);

		return new OrSpecification({ specifications });
	}

	setDimensionIdsFromSpecification(specification) {
		if (specification && specification.isOr()) {
			this._dimensionIds = specification
				.getSpecifications()
				.map((spec) => spec.getDimensionId());
		} else if (specification && specification.getDimensionId) {
			this._dimensionIds = [specification.getDimensionId()];
		} else {
			this._dimensionIds = [];
		}
	}

	setDimensionIds(dimensionIds) {
		this._dimensionIds = dimensionIds;
	}

	validate({ collector }) {
		if (this._dimensionIds.length !== 0) {
			return;
		}
		collector.addError(_("Missing dimensions on repeating row"));
	}

	canAdd(row) {
		return super.canAdd(...arguments) && row.canBeRepeated();
	}

	canBeRepeated() {
		return false;
	}

	canBeAddedToGroupRow(group) {
		return super.canBeAddedToGroupRow(...arguments) && !group.isRepeated();
	}

	isRepeatingRowTemplate() {
		return true;
	}

	accept(visitor) {
		return visitor.visitRepeatingRowTemplate(this);
	}

	_instanceClass() {
		return RepeatingRow;
	}

	_interpretationSpec(interpreter) {
		const spec = super._interpretationSpec(...arguments);
		const context = interpreter.getContext();

		const dimensions = context
			.findDimensions(this._dimensionIds)
			.reduce((acc, dimension) => {
				acc.push(...dimension.getLeaves());
				return acc;
			}, []);

		spec.rows = dimensions.flatMap((dimension) =>
			this._interpretRowsForDimension(dimension, context, interpreter),
		);

		return spec;
	}

	_interpretRowsForDimension(dimension, context, interpreter) {
		const rowContext = this._copyContextForDimension(dimension, context);
		interpreter = new interpreter.constructor({ context: rowContext });
		return interpreter.interpretItemTemplates(this._rows);
	}

	_copyContextForDimension(dimension, context) {
		const dimensionSettings = new Map();

		this._rows.forEach((rowTemplate) => {
			dimensionSettings.set(rowTemplate, {
				dimensionSpecification: new DimensionSpecification({
					dimensionId: dimension.getId(),
				}),
			});
		});

		return context.cloneWithAdditionalSettings(dimensionSettings);
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.dimensionIds = this._dimensionIds;

		return properties;
	}

	static getLabel() {
		return _("Repeating");
	}

	static getType() {
		return "RepeatingRowTemplate";
	}
}
